/**
 * staffStore — the single Zustand store for the staff panel.
 *
 * Combines staff auth/profile, dashboard, moderation, categories, notifications,
 * emails, Exam PINs, and purchase slices with dedicated selector hooks.
 */
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import authSession from "../../lib/authSession";

import { staffAuthInitialState, createStaffAuthActions } from "./staffAuthStore";
import { staffDashboardInitialState, createStaffDashboardActions } from "./staffDashboardStore";
import { staffEmailInitialState, createStaffEmailActions } from "./staffEmailStore";
import { staffExamPinInitialState, createStaffExamPinActions } from "./staffExamPinStore";
import {
  staffExamPurchaseInitialState,
  createStaffExamPurchaseActions,
} from "./staffExamPurchaseStore";
import { staffContentInitialState, createStaffContentActions } from "./staffContentStore";
import { staffCategoryInitialState, createStaffCategoryActions } from "./staffCategoryStore";
import {
  staffNotificationInitialState,
  createStaffNotificationActions,
} from "./staffNotificationStore";
import {
  staffDepartmentInitialState,
  createStaffDepartmentActions,
} from "./staffDepartmentStore";
import {
  staffMarketplaceInitialState,
  createStaffMarketplaceActions,
} from "./staffMarketplaceStore";
import { newsInitialState, createNewsActions } from "../admin/adminNewsStore";

export const useStaffStore = create((set, get) => ({
  // Slice state
  ...staffAuthInitialState,
  ...staffDashboardInitialState,
  ...staffEmailInitialState,
  ...staffExamPinInitialState,
  ...staffExamPurchaseInitialState,
  ...staffContentInitialState,
  ...staffCategoryInitialState,
  ...staffNotificationInitialState,
  ...staffDepartmentInitialState,
  ...staffMarketplaceInitialState,
  ...newsInitialState,

  // Shared message state
  successMessage: null,
  errorMessage: null,

  // Slice actions
  ...createStaffAuthActions(set, get),
  ...createStaffDashboardActions(set, get),
  ...createStaffEmailActions(set, get),
  ...createStaffExamPinActions(set, get),
  ...createStaffExamPurchaseActions(set, get),
  ...createStaffContentActions(set, get),
  ...createStaffCategoryActions(set, get),
  ...createStaffNotificationActions(set, get),
  ...createStaffDepartmentActions(set, get),
  ...createStaffMarketplaceActions(set, get),
  // Shares the admin news contract and action logic, scoped to staff routes.
  ...createNewsActions(
    set,
    () => ({ token: get().staffToken || authSession.getToken() }),
    "/api/staff/news"
  ),

  // Shared helpers
  getToken: () => get().staffToken || authSession.getToken(),
  clearMessage: () => set({ successMessage: null, errorMessage: null }),
}));

// ── Selector hooks ───────────────────────────────────────────────────────────

/** Auth + profile selectors. */
export const useStaffAuth = () =>
  useStaffStore(
    useShallow((s) => ({
      staffIsAuthenticated: s.staffIsAuthenticated,
      staffUser: s.staffUser,
      staffToken: s.staffToken,
      staffProfile: s.staffProfile,
      staffProfileLoading: s.staffProfileLoading,
      staffProfileError: s.staffProfileError,
      staffLoginLoading: s.staffLoginLoading,
      staffLoginError: s.staffLoginError,
      staffAuthActionLoading: s.staffAuthActionLoading,
      staffAuthActionError: s.staffAuthActionError,
      staffLogin: s.staffLogin,
      staffLogout: s.staffLogout,
      staffRefresh: s.staffRefresh,
      staffChangePassword: s.staffChangePassword,
      staffForgotPassword: s.staffForgotPassword,
      staffResetPassword: s.staffResetPassword,
      fetchStaffProfile: s.fetchStaffProfile,
      updateStaffProfile: s.updateStaffProfile,
    }))
  );

/** Dashboard selectors. */
export const useStaffDashboard = () =>
  useStaffStore(
    useShallow((s) => ({
      staffDashboard: s.staffDashboard,
      staffDashboardLoading: s.staffDashboardLoading,
      staffDashboardError: s.staffDashboardError,
      fetchStaffDashboard: s.fetchStaffDashboard,
    }))
  );

/** Email-delivery selectors. */
export const useStaffEmails = () =>
  useStaffStore(
    useShallow((s) => ({
      staffEmails: s.staffEmails,
      staffEmailsLoading: s.staffEmailsLoading,
      staffEmailsError: s.staffEmailsError,
      staffEmailsTotal: s.staffEmailsTotal,
      staffEmailsPages: s.staffEmailsPages,
      fetchStaffEmails: s.fetchStaffEmails,
      staffEmailDetail: s.staffEmailDetail,
      staffEmailDetailLoading: s.staffEmailDetailLoading,
      staffEmailDetailError: s.staffEmailDetailError,
      fetchStaffEmail: s.fetchStaffEmail,
      staffFailedEmails: s.staffFailedEmails,
      staffFailedEmailsLoading: s.staffFailedEmailsLoading,
      staffFailedEmailsError: s.staffFailedEmailsError,
      staffFailedEmailsTotal: s.staffFailedEmailsTotal,
      staffFailedEmailsPages: s.staffFailedEmailsPages,
      fetchStaffFailedEmails: s.fetchStaffFailedEmails,
      staffEmailMutationLoading: s.staffEmailMutationLoading,
      staffEmailMutationError: s.staffEmailMutationError,
      retryStaffEmail: s.retryStaffEmail,
    }))
  );

/** Exam PIN inventory selectors. */
export const useStaffExamPins = () =>
  useStaffStore(
    useShallow((s) => ({
      staffExamPins: s.staffExamPins,
      staffExamPinsLoading: s.staffExamPinsLoading,
      staffExamPinsError: s.staffExamPinsError,
      staffExamPinsTotal: s.staffExamPinsTotal,
      staffExamPinsPages: s.staffExamPinsPages,
      fetchStaffExamPins: s.fetchStaffExamPins,
      staffExamPinDetail: s.staffExamPinDetail,
      staffExamPinDetailLoading: s.staffExamPinDetailLoading,
      staffExamPinDetailError: s.staffExamPinDetailError,
      fetchStaffExamPin: s.fetchStaffExamPin,
      staffExamPinDeliveries: s.staffExamPinDeliveries,
      staffExamPinDeliveriesLoading: s.staffExamPinDeliveriesLoading,
      staffExamPinDeliveriesError: s.staffExamPinDeliveriesError,
      staffExamPinDeliveriesTotal: s.staffExamPinDeliveriesTotal,
      staffExamPinDeliveriesPages: s.staffExamPinDeliveriesPages,
      fetchStaffExamPinDeliveries: s.fetchStaffExamPinDeliveries,
      staffExamPinStats: s.staffExamPinStats,
      staffExamPinStatsLoading: s.staffExamPinStatsLoading,
      staffExamPinStatsError: s.staffExamPinStatsError,
      fetchStaffExamPinStats: s.fetchStaffExamPinStats,
      staffExamPinMutationLoading: s.staffExamPinMutationLoading,
      staffExamPinMutationError: s.staffExamPinMutationError,
      uploadStaffExamPins: s.uploadStaffExamPins,
      deleteStaffExamPin: s.deleteStaffExamPin,
    }))
  );

/** Exam purchase selectors. */
export const useStaffExamPurchases = () =>
  useStaffStore(
    useShallow((s) => ({
      staffExamPurchases: s.staffExamPurchases,
      staffExamPurchasesLoading: s.staffExamPurchasesLoading,
      staffExamPurchasesError: s.staffExamPurchasesError,
      staffExamPurchasesTotal: s.staffExamPurchasesTotal,
      staffExamPurchasesPages: s.staffExamPurchasesPages,
      fetchStaffExamPurchases: s.fetchStaffExamPurchases,
      staffExamPurchaseDetail: s.staffExamPurchaseDetail,
      staffExamPurchaseDetailLoading: s.staffExamPurchaseDetailLoading,
      staffExamPurchaseDetailError: s.staffExamPurchaseDetailError,
      fetchStaffExamPurchase: s.fetchStaffExamPurchase,
      staffExamPurchaseStats: s.staffExamPurchaseStats,
      staffExamPurchaseStatsLoading: s.staffExamPurchaseStatsLoading,
      staffExamPurchaseStatsError: s.staffExamPurchaseStatsError,
      fetchStaffExamPurchaseStats: s.fetchStaffExamPurchaseStats,
      staffExamPurchaseMutationLoading: s.staffExamPurchaseMutationLoading,
      staffExamPurchaseMutationError: s.staffExamPurchaseMutationError,
      retryStaffExamPurchaseFulfillment: s.retryStaffExamPurchaseFulfillment,
    }))
  );

/** Content moderation selectors. */
export const useStaffContent = () =>
  useStaffStore(
    useShallow((s) => ({
      staffContentItems: s.staffContentItems,
      staffContentLoading: s.staffContentLoading,
      staffContentError: s.staffContentError,
      staffContentTotal: s.staffContentTotal,
      staffContentPages: s.staffContentPages,
      staffContentDetail: s.staffContentDetail,
      staffContentDetailLoading: s.staffContentDetailLoading,
      staffContentDetailError: s.staffContentDetailError,
      staffContentMutationLoading: s.staffContentMutationLoading,
      staffContentMutationError: s.staffContentMutationError,
      staffPendingCount: s.staffPendingCount,
      fetchStaffPendingCount: s.fetchStaffPendingCount,
      fetchStaffContent: s.fetchStaffContent,
      clearStaffContent: s.clearStaffContent,
      fetchStaffContentDetail: s.fetchStaffContentDetail,
      updateStaffContent: s.updateStaffContent,
      approveStaffContent: s.approveStaffContent,
      rejectStaffContent: s.rejectStaffContent,
      disableStaffCourse: s.disableStaffCourse,
      featureStaffCourse: s.featureStaffCourse,
      unfeatureStaffCourse: s.unfeatureStaffCourse,
    }))
  );

/** Course category selectors. */
export const useStaffCategories = () =>
  useStaffStore(
    useShallow((s) => ({
      staffCategories: s.staffCategories,
      staffCategoriesLoading: s.staffCategoriesLoading,
      staffCategoriesError: s.staffCategoriesError,
      staffCategoryDetail: s.staffCategoryDetail,
      staffCategoryDetailLoading: s.staffCategoryDetailLoading,
      staffCategoryDetailError: s.staffCategoryDetailError,
      staffCategoryMutationLoading: s.staffCategoryMutationLoading,
      staffCategoryMutationError: s.staffCategoryMutationError,
      fetchStaffCategories: s.fetchStaffCategories,
      fetchStaffCategory: s.fetchStaffCategory,
      createStaffCategory: s.createStaffCategory,
      updateStaffCategory: s.updateStaffCategory,
      deleteStaffCategory: s.deleteStaffCategory,
    }))
  );

/** In-app notification selectors. */
export const useStaffNotifications = () =>
  useStaffStore(
    useShallow((s) => ({
      staffNotifications: s.staffNotifications,
      staffNotificationsLoading: s.staffNotificationsLoading,
      staffNotificationsError: s.staffNotificationsError,
      staffNotificationsTotal: s.staffNotificationsTotal,
      staffNotificationsPages: s.staffNotificationsPages,
      staffNotificationDetail: s.staffNotificationDetail,
      staffNotificationDetailLoading: s.staffNotificationDetailLoading,
      staffNotificationDetailError: s.staffNotificationDetailError,
      staffNotificationUnreadCount: s.staffNotificationUnreadCount,
      staffNotificationUnreadCountLoading: s.staffNotificationUnreadCountLoading,
      staffNotificationUnreadCountError: s.staffNotificationUnreadCountError,
      staffNotificationMutationLoading: s.staffNotificationMutationLoading,
      staffNotificationMutationError: s.staffNotificationMutationError,
      fetchStaffNotifications: s.fetchStaffNotifications,
      fetchStaffNotification: s.fetchStaffNotification,
      fetchStaffNotificationUnreadCount: s.fetchStaffNotificationUnreadCount,
      markStaffNotificationRead: s.markStaffNotificationRead,
      markAllStaffNotificationsRead: s.markAllStaffNotificationsRead,
    }))
  );

/** Department selectors. */
export const useStaffDepartments = () =>
  useStaffStore(
    useShallow((s) => ({
      staffDepartments: s.staffDepartments,
      staffDepartmentsLoading: s.staffDepartmentsLoading,
      staffDepartmentsError: s.staffDepartmentsError,
      staffDepartmentMutationLoading: s.staffDepartmentMutationLoading,
      staffDepartmentMutationError: s.staffDepartmentMutationError,
      fetchStaffDepartments: s.fetchStaffDepartments,
      createStaffDepartment: s.createStaffDepartment,
      updateStaffDepartment: s.updateStaffDepartment,
      deleteStaffDepartment: s.deleteStaffDepartment,
    }))
  );

/** Marketplace selectors (resources + purchases). */
export const useStaffMarketplace = () =>
  useStaffStore(
    useShallow((s) => ({
      // Resources
      staffMarketplaceResources: s.staffMarketplaceResources,
      staffMarketplaceResourcesType: s.staffMarketplaceResourcesType,
      staffMarketplaceResourcesLoading: s.staffMarketplaceResourcesLoading,
      staffMarketplaceResourcesError: s.staffMarketplaceResourcesError,
      staffMarketplaceResourcesTotal: s.staffMarketplaceResourcesTotal,
      staffMarketplaceResourcesPages: s.staffMarketplaceResourcesPages,
      fetchStaffMarketplaceResources: s.fetchStaffMarketplaceResources,
      // Single resource
      staffMarketplaceResourceDetail: s.staffMarketplaceResourceDetail,
      staffMarketplaceResourceDetailLoading: s.staffMarketplaceResourceDetailLoading,
      staffMarketplaceResourceDetailError: s.staffMarketplaceResourceDetailError,
      fetchStaffMarketplaceResource: s.fetchStaffMarketplaceResource,
      // Resource mutations
      updateStaffMarketplaceResource: s.updateStaffMarketplaceResource,
      approveStaffMarketplaceResource: s.approveStaffMarketplaceResource,
      rejectStaffMarketplaceResource: s.rejectStaffMarketplaceResource,
      restoreStaffMarketplaceResource: s.restoreStaffMarketplaceResource,
      withdrawStaffMarketplaceResource: s.withdrawStaffMarketplaceResource,
      // Purchases
      staffMarketplacePurchases: s.staffMarketplacePurchases,
      staffMarketplacePurchasesLoading: s.staffMarketplacePurchasesLoading,
      staffMarketplacePurchasesError: s.staffMarketplacePurchasesError,
      staffMarketplacePurchasesTotal: s.staffMarketplacePurchasesTotal,
      staffMarketplacePurchasesPages: s.staffMarketplacePurchasesPages,
      fetchStaffMarketplacePurchases: s.fetchStaffMarketplacePurchases,
      revokeStaffMarketplacePurchase: s.revokeStaffMarketplacePurchase,
      restoreStaffMarketplacePurchase: s.restoreStaffMarketplacePurchase,
      // Mutation flags
      staffMarketplaceMutationLoading: s.staffMarketplaceMutationLoading,
      staffMarketplaceMutationError: s.staffMarketplaceMutationError,
    }))
  );

/** News selectors; endpoints are the staff-prefixed admin news contract. */
export const useStaffNews = () =>
  useStaffStore(
    useShallow((s) => ({
      news: s.news,
      newsLoading: s.newsLoading,
      newsError: s.newsError,
      newsTotal: s.newsTotal,
      newsPages: s.newsPages,
      fetchNews: s.fetchNews,
      newsDetail: s.newsDetail,
      newsDetailLoading: s.newsDetailLoading,
      newsDetailError: s.newsDetailError,
      fetchNewsById: s.fetchNewsById,
      createNews: s.createNews,
      updateNews: s.updateNews,
      deleteNewsById: s.deleteNewsById,
      publishNews: s.publishNews,
      unpublishNews: s.unpublishNews,
      featureNews: s.featureNews,
      unfeatureNews: s.unfeatureNews,
      newsCategories: s.newsCategories,
      newsCategoriesLoading: s.newsCategoriesLoading,
      newsCategoriesError: s.newsCategoriesError,
      fetchNewsCategories: s.fetchNewsCategories,
      createNewsCategory: s.createNewsCategory,
      updateNewsCategory: s.updateNewsCategory,
      deleteNewsCategory: s.deleteNewsCategory,
      newsMutationLoading: s.newsMutationLoading,
      newsMutationError: s.newsMutationError,
    }))
  );

/** Generic messages + token helpers. */
export const useStaffMessages = () =>
  useStaffStore(
    useShallow((s) => ({
      successMessage: s.successMessage,
      errorMessage: s.errorMessage,
      clearMessage: s.clearMessage,
      getToken: s.getToken,
    }))
  );

export default useStaffStore;
